import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { MessageCircle, MapPin, Sparkle } from 'lucide-react';
import { BRAND, IMAGES, AREAS, scrollToId } from '../config/site';

const lineAnim = (i) => ({
  initial: { y: '112%' },
  animate: { y: 0 },
  transition: { duration: 0.85, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  const imgWrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgWrapRef,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);

  return (
    <section id="top" className="ks-hero relative flex flex-col-reverse overflow-hidden lg:block">
      <div
        className="pointer-events-none absolute -left-40 top-24 h-[480px] w-[480px] rounded-full bg-aqua/15 blur-3xl"
        aria-hidden="true"
      />

      {/* Desktop — immersive image panel */}
      <motion.div
        ref={imgWrapRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.25 }}
        className="absolute inset-y-0 right-0 hidden w-[47%] lg:block"
        data-testid="hero-image-panel"
      >
        <motion.img
          style={{ y, scale: 1.14 }}
          src={IMAGES.hero}
          alt="Bright residential kitchen being professionally cleaned in natural daylight"
          className="h-full w-full object-cover"
          loading="eager"
        />
        <svg
          viewBox="0 0 100 800"
          preserveAspectRatio="none"
          className="absolute inset-y-0 left-0 h-full w-[90px]"
          aria-hidden="true"
        >
          <path
            d="M0,0 L100,0 C30,180 115,360 42,540 C12,655 62,730 100,800 L0,800 Z"
            fill="#E1F2FF"
          />
        </svg>
      </motion.div>

      {/* Mobile — reassuring home image follows the headline and primary CTA */}
      <div className="relative h-[300px] w-full sm:h-[420px] lg:hidden" data-testid="hero-image-panel-mobile">
        <img
          src={IMAGES.hero}
          alt="Professional cleaning in a bright residential kitchen"
          className="h-full w-full object-cover"
          loading="eager"
        />
        <svg
          viewBox="0 0 390 48"
          preserveAspectRatio="none"
          className="absolute bottom-0 left-0 h-10 w-full"
          aria-hidden="true"
        >
          <path d="M0,48 L0,20 C90,4 200,0 260,10 C310,18 360,20 390,14 L390,48 Z" fill="#E1F2FF" />
        </svg>
      </div>

      {/* Copy */}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl pb-9 pt-28 sm:pb-12 sm:pt-28 lg:min-h-[680px] lg:w-[46%] lg:pt-28 lg:pb-16">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-navy"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-aqua" aria-hidden="true" />
            <span className="whitespace-nowrap">Residential Cleaning · Central Florida</span>
          </motion.p>

          <h1
            className="mt-6 text-4xl font-extrabold leading-[1.06] tracking-tight text-navy sm:text-5xl lg:text-6xl"
            data-testid="hero-headline"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(0)}>
                A Cleaner Home.
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(1)}>
                More Time for
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span className="block text-electric" {...lineAnim(2)}>
                What Matters.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-6 max-w-md text-base leading-relaxed text-charcoal sm:text-lg"
            data-testid="hero-subheadline"
          >
            Professional residential cleaning designed around your home, your schedule, and the level
            of care you expect.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.72 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <button
              type="button"
              data-testid="hero-quote-button"
              onClick={() => scrollToId('quote')}
              className="rounded-full bg-electric px-8 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-soft transition-all hover:-translate-y-0.5 hover:bg-navy"
            >
              Get a Free Quote
            </button>
            <a
              href={BRAND.smsHref}
              data-testid="hero-text-button"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-navy/15 bg-white px-8 py-4 text-sm font-bold uppercase tracking-wide text-navy transition-all hover:-translate-y-0.5 hover:border-electric hover:text-electric"
            >
              <MessageCircle size={16} />
              Text Us
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.85 }}
            className="mt-10 border-t border-navy/10 pt-6"
          >
            <p className="inline-flex items-center gap-2 text-sm font-semibold text-navy">
              <MapPin size={15} className="text-fresh" aria-hidden="true" />
              {BRAND.location}
            </p>
            <p className="mt-2 flex items-center gap-2 text-sm font-medium text-charcoal/70">
              <Sparkle size={13} className="shrink-0 text-aqua" aria-hidden="true" />
              {AREAS.join(' · ')}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
